import React from "react";
import { Check, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PayPalUserPaymentOption from "@/components/billing/PayPalUserPaymentOption";
import { getTranslationsForLanguage } from "@/i18n";

export default function PricingPlanCard({ plan, isCurrent, isPopular, copy, paypalCopy }) {
  const fallback = getTranslationsForLanguage("en", "billing");
  const labels = copy || fallback.pricing;
  const paypalLabels = paypalCopy || fallback.paypal;

  return (
    <Card className={`relative flex h-full min-w-0 flex-col overflow-hidden rounded-[1.75rem] border-white/70 bg-white/95 shadow-xl shadow-ocean/10 ${isPopular ? "ring-2 ring-primary shadow-2xl shadow-primary/20" : ""} ${isCurrent ? "border-success/50" : ""}`}>
      {isPopular && (
        <div className="absolute inset-x-0 top-0 bg-gradient-to-r from-primary to-accent px-4 py-2.5 text-center text-[11px] font-black uppercase tracking-[0.18em] text-white">
          {labels.bestValue}
        </div>
      )}
      <CardHeader className={`space-y-5 p-6 ${isPopular ? "pt-14" : "pt-6"}`}>
        <div className="flex min-w-0 items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="break-words text-2xl font-black leading-tight text-foreground">{plan.name}</CardTitle>
            <p className="mt-3 min-h-[3.5rem] break-words text-sm leading-7 text-muted-foreground">{plan.description}</p>
          </div>
          {isCurrent && <Badge className="shrink-0 bg-success text-white">{labels.current}</Badge>}
        </div>
        <div>
          <div className="flex flex-wrap items-end gap-x-2 gap-y-1">
            <span className="text-5xl font-black leading-none tracking-tight text-foreground">${plan.monthly_price}</span>
            <span className="pb-1 text-sm text-muted-foreground">{labels.perMonth}</span>
          </div>
          {plan.yearly_price && (
            <p className="mt-2 text-xs font-bold text-primary">{labels.yearly.replace(/\{price\}|\$\{price\}/g, plan.yearly_price)}</p>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-5 p-6 pt-0">
        <div className="rounded-2xl bg-sand/45 p-4">
          <p className="flex items-center gap-2 text-sm font-black leading-6"><Sparkles className="h-4 w-4 shrink-0 text-primary" /> <span>{labels.included}</span></p>
          {(plan.features || []).length > 0 && (
            <ul className="mt-3 space-y-2">
              {(plan.features || []).slice(0, 6).map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm leading-6">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-success" />
                  <span className="break-words">{feature}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="mt-auto">
          {isCurrent ? (
            <div className="rounded-2xl border border-success/30 bg-success/10 p-4 text-center text-sm font-bold leading-6 text-success">{labels.currentPlanNote}</div>
          ) : (
            <PayPalUserPaymentOption planName={plan.name} planTier={plan.tier} price={plan.monthly_price} copy={paypalLabels} priceSuffix={labels.perMonth} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}